<template>
  <div class="tutorial-wizard">
    <!-- Tutorial Header -->
    <div class="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-t-xl">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-bold flex items-center gap-3">
          <IconifyIcon icon="heroicons:academic-cap-20-solid" class="w-8 h-8" />
          WebRTC QR Tutorial
        </h2>
        <button
          @click="closeTutorial"
          class="p-2 hover:bg-white/20 rounded-lg transition-colors"
          title="Close tutorial"
        >
          <IconifyIcon icon="heroicons:x-mark-20-solid" class="w-6 h-6" />
        </button>
      </div>

      <!-- Progress Bar -->
      <div class="mb-4">
        <div class="flex items-center justify-between text-sm mb-2">
          <span>Step {{ currentStep + 1 }} of {{ steps.length }}</span>
          <span>{{ Math.round(((currentStep + 1) / steps.length) * 100) }}% Complete</span>
        </div>
        <div class="w-full bg-white/20 rounded-full h-2">
          <div
            class="bg-white rounded-full h-2 transition-all duration-500 ease-out"
            :style="{ width: `${((currentStep + 1) / steps.length) * 100}%` }"
          ></div>
        </div>
      </div>

      <!-- Step Indicators -->
      <div class="flex items-center justify-center gap-2 flex-wrap">
        <button
          v-for="(step, index) in steps"
          :key="index"
          @click="goToStep(index)"
          :class="[
            'w-3 h-3 rounded-full transition-all duration-200',
            index === currentStep ? 'bg-white scale-125' :
            index < currentStep ? 'bg-white/70' : 'bg-white/30'
          ]"
          :title="step.title"
        ></button>
      </div>
    </div>

    <!-- Tutorial Content -->
    <div class="bg-white p-6">
      <transition
        name="step-fade"
        mode="out-in"
        appear
      >
        <div :key="currentStep">
          <!-- Current Step -->
          <div class="mb-6">
            <div class="flex items-center gap-3 mb-4">
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                :style="{ backgroundColor: steps[currentStep].color }"
              >
                {{ currentStep + 1 }}
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900">{{ steps[currentStep].title }}</h3>
                <p class="text-sm text-gray-600">{{ steps[currentStep].subtitle }}</p>
              </div>
            </div>

            <!-- Step Description -->
            <div class="prose prose-indigo max-w-none mb-6">
              <p class="text-gray-700 leading-relaxed">{{ steps[currentStep].description }}</p>
            </div>

            <!-- Interactive Content Based on Step -->
            <div class="bg-gray-50 rounded-lg p-6">
              <component
                :is="steps[currentStep].component"
                v-bind="steps[currentStep].componentProps"
                @step-action="handleStepAction"
              />
            </div>

            <!-- Step Tips -->
            <div v-if="steps[currentStep].tips" class="mt-4">
              <div class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <IconifyIcon icon="heroicons:information-circle-20-solid" class="w-5 h-5 text-blue-400" />
                  </div>
                  <div class="ml-3">
                    <p class="text-sm text-blue-700">
                      <strong>💡 Tip:</strong> {{ steps[currentStep].tips }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Navigation Buttons -->
      <div class="flex items-center justify-between pt-6 border-t">
        <button
          @click="previousStep"
          :disabled="currentStep === 0"
          :class="[
            'px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2',
            currentStep === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          ]"
        >
          <IconifyIcon icon="heroicons:arrow-left-20-solid" class="w-5 h-5" />
          Previous
        </button>

        <div class="flex items-center gap-2">
          <button
            @click="toggleAutoPlay"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              autoPlay
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            ]"
          >
            <IconifyIcon :icon="autoPlay ? 'heroicons:pause-20-solid' : 'heroicons:play-20-solid'" class="w-4 h-4" />
            {{ autoPlay ? 'Pause' : 'Auto Play' }}
          </button>
        </div>

        <button
          @click="nextStep"
          :disabled="currentStep === steps.length - 1"
          :class="[
            'px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700',
            currentStep === steps.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
          ]"
        >
          {{ currentStep === steps.length - 1 ? 'Complete' : 'Next' }}
          <IconifyIcon icon="heroicons:arrow-right-20-solid" class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- Tutorial Completion -->
    <div v-if="showCompletion" class="bg-green-50 border-t-4 border-green-400 p-6">
      <div class="flex">
        <div class="flex-shrink-0">
          <IconifyIcon icon="heroicons:check-circle-20-solid" class="w-6 h-6 text-green-400" />
        </div>
        <div class="ml-3">
          <h3 class="text-lg font-medium text-green-800">Tutorial Complete!</h3>
          <div class="mt-2 text-sm text-green-700">
            <p>You've successfully learned how to use WebRTC QR connections. You can now:</p>
            <ul class="list-disc list-inside mt-2 space-y-1">
              <li>Create host connections with QR codes</li>
              <li>Scan QR codes to join as a client</li>
              <li>Establish secure P2P connections</li>
              <li>Transfer data and communicate in real-time</li>
            </ul>
          </div>
          <div class="mt-4">
            <button
              @click="() => startRealDemo()"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Start Real Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type Component } from 'vue'
import IconifyIcon from './IconifyIcon.vue'
import TutorialIntro from './tutorial-steps/TutorialIntro.vue'
import TutorialHost from './tutorial-steps/TutorialHost.vue'
import TutorialQR from './tutorial-steps/TutorialQR.vue'
import TutorialClient from './tutorial-steps/TutorialClient.vue'
import TutorialConnection from './tutorial-steps/TutorialConnection.vue'
import TutorialFeatures from './tutorial-steps/TutorialFeatures.vue'

interface TutorialStep {
  title: string
  subtitle: string
  description: string
  component: Component
  componentProps?: Record<string, unknown>
  tips?: string
  color: string
  duration?: number
}

// Props & Emits
const emit = defineEmits<{
  close: []
  startDemo: [mode: 'host' | 'client']
}>()

// Reactive state
const currentStep = ref(0)
const autoPlay = ref(false)
const autoPlayTimer = ref<number | null>(null)
const showCompletion = ref(false)

// Tutorial steps
const steps: TutorialStep[] = [
  {
    title: "Welcome to WebRTC QR",
    subtitle: "Getting started with peer-to-peer connections",
    description: "WebRTC QR Tool enables secure, serverless peer-to-peer connections using QR codes for signaling. No servers, no registration - just direct browser-to-browser communication.",
    component: TutorialIntro,
    color: "#6366f1",
    tips: "This tutorial will guide you through creating and joining WebRTC connections step by step.",
    duration: 5000
  },
  {
    title: "Creating a Host Connection",
    subtitle: "Start by hosting a connection",
    description: "As a host, you'll create a WebRTC connection and generate a QR code that others can scan to join. Your browser becomes the server!",
    component: TutorialHost,
    componentProps: { onNext: () => nextStep() },
    color: "#8b5cf6",
    tips: "You can customize your connection with a room name and specific features like video, audio, or data channels.",
    duration: 8000
  },
  {
    title: "Understanding QR Codes",
    subtitle: "The magic of QR-based signaling",
    description: "The QR code contains all the information needed for another device to connect directly with you. It's like a digital handshake!",
    component: TutorialQR,
    color: "#ec4899",
    tips: "Each QR code contains encrypted connection details including ICE candidates and session descriptions.",
    duration: 6000
  },
  {
    title: "Joining as a Client",
    subtitle: "Scan and connect",
    description: "Use your camera to scan the host's QR code, or manually enter the connection string. Your device will establish a direct connection with the host.",
    component: TutorialClient,
    componentProps: { onConnect: () => nextStep() },
    color: "#f59e0b",
    tips: "Make sure to allow camera permissions when scanning QR codes. The connection works entirely in your browser!",
    duration: 7000
  },
  {
    title: "Establishing Connection",
    subtitle: "Direct P2P communication",
    description: "Watch as the devices establish a direct WebRTC connection. You'll see real-time status updates and connection metrics.",
    component: TutorialConnection,
    color: "#10b981",
    tips: "The connection may take a few moments as devices negotiate the best path for communication.",
    duration: 5000
  },
  {
    title: "Explore Features",
    subtitle: "What you can do with WebRTC QR",
    description: "Once connected, you can chat, transfer files, have video calls, and share data - all without any servers in between!",
    component: TutorialFeatures,
    componentProps: { onSelectFeature: (feature: string) => console.log('Selected:', feature) },
    color: "#ef4444",
    tips: "Try different features like chat, file transfer, or video conferencing to see what works best for your use case.",
    duration: 8000
  }
]

// Computed
const currentStepData = computed(() => steps[currentStep.value])

// Methods
const nextStep = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
    resetAutoPlay()
  } else {
    completeTutorial()
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
    resetAutoPlay()
  }
}

const goToStep = (index: number) => {
  currentStep.value = index
  resetAutoPlay()
}

const handleStepAction = (action: unknown) => {
  console.log('Step action:', action)
  // Handle specific actions from tutorial steps
}

const toggleAutoPlay = () => {
  autoPlay.value = !autoPlay.value
  if (autoPlay.value) {
    startAutoPlay()
  } else {
    stopAutoPlay()
  }
}

const startAutoPlay = () => {
  if (currentStepData.value.duration) {
    autoPlayTimer.value = window.setTimeout(() => {
      nextStep()
      if (autoPlay.value && currentStep.value < steps.length - 1) {
        startAutoPlay()
      }
    }, currentStepData.value.duration)
  }
}

const stopAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearTimeout(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

const resetAutoPlay = () => {
  stopAutoPlay()
  if (autoPlay.value) {
    startAutoPlay()
  }
}

const completeTutorial = () => {
  showCompletion.value = true
  stopAutoPlay()
  autoPlay.value = false
}

const closeTutorial = () => {
  stopAutoPlay()
  emit('close')
}

const startRealDemo = (mode: 'host' | 'client' = 'host') => {
  emit('startDemo', mode)
  closeTutorial()
}

// Lifecycle
onMounted(() => {
  // Start with autoplay disabled
  autoPlay.value = false
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<style scoped>
/* Step transitions */
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.step-fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.step-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Custom scrollbar */
.prose {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.prose::-webkit-scrollbar {
  height: 6px;
}

.prose::-webkit-scrollbar-track {
  background: transparent;
}

.prose::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 3px;
}

.prose::-webkit-scrollbar-thumb:hover {
  background-color: #d1d5db;
}
</style>